import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { Button } from "@/components/ui/button";
import { DeliveryTrackingFinalStateOverlay } from "@/features/delivery/components/delivery-tracking-final-state-overlay";
import { DeliveryTrackingLegend } from "@/features/delivery/components/delivery-tracking-legend";
import { DeliveryTrackingMap } from "@/features/delivery/components/delivery-tracking-map";
import { useDeliveryEvents } from "@/features/delivery/hooks/use-delivery-events";
import {
  useDeliveryForOrder,
  useDeliveryTracking,
} from "@/features/delivery/hooks/use-delivery-tracking";
import {
  buildDeliveryTrackingMapState,
  type DeliveryTrackingNoticeCode,
} from "@/features/delivery/utils/tracking-map-state";
import { authClient } from "@/lib/auth-client";

const NOTICE_KEYS: Record<DeliveryTrackingNoticeCode, string> = {
  loading: "tracking.notice.loading",
  preparing: "tracking.notice.preparing",
  waitingAcceptance: "tracking.notice.waitingAcceptance",
  waitingRiderLocation: "tracking.notice.waitingRiderLocation",
  staleRiderLocation: "tracking.notice.staleRiderLocation",
  missingDestination: "tracking.notice.missingDestination",
  missingOrderPoints: "tracking.notice.missingOrderPoints",
};

// =============================================================================
// Issue #82 — Delivery Tracking empty/error/final-state UX
//
// The tracking page now treats every non-happy path explicitly:
//   * No Delivery for the Order → a clear not-found state with back nav.
//   * Tracking query failure → an error state with a retry CTA.
//   * Loading → the spinner stays, and once we know there's a Delivery but no
//     tracking data yet, the helper surfaces a `loading` notice copy.
//   * `unassigned` / `assigned` / active-without-rider / stale rider / missing
//     destination / missing order points → translated notice banner over a
//     still-rendered map.
//   * Terminal states (`delivered` / `cancelled` / `failed`) → a final-state
//     overlay that keeps the map visible underneath, with a session-aware
//     CTA (`Calificar pedido` for authenticated delivered; `Volver al pedido`
//     otherwise).
//
// The map-state helper (see `tracking-map-state.ts`) owns all the
// state-machine rules; this route is responsible for translating codes into
// copy via i18n and rendering the right shell for each branch.
// =============================================================================

export default function DeliveryTrackingPage() {
  const { t } = useTranslation();
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();

  const { data: session, isPending: sessionPending } = authClient.useSession();
  const deliveryQuery = useDeliveryForOrder(id);
  const deliveryId = deliveryQuery.data?.id;
  const tracking = useDeliveryTracking(deliveryId ?? undefined);
  useDeliveryEvents(deliveryId ?? undefined);

  const backHref = `/m/${orgSlug}/delivery/order/${id}`;

  // --- Not yet resolved: which Delivery belongs to this Order? -------------
  if (deliveryQuery.isPending) {
    return (
      <TrackingPageShell backHref={backHref}>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </TrackingPageShell>
    );
  }

  if (deliveryQuery.isError) {
    return (
      <TrackingPageShell backHref={backHref}>
        <TrackingErrorState
          onRetry={() => {
            void deliveryQuery.refetch();
          }}
        />
      </TrackingPageShell>
    );
  }

  // The backend can either confirm there is no Delivery row for this Order
  // (returns `{ delivery: null }`) or fail with 404 / 403 — all three
  // branches must lead to the same not-found surface so the customer never
  // sees a broken map.
  const deliveryMissing = deliveryQuery.data === null || deliveryQuery.data === undefined;
  if (deliveryMissing || !deliveryId) {
    return (
      <TrackingPageShell backHref={backHref}>
        <TrackingNotFoundState />
      </TrackingPageShell>
    );
  }

  // --- We have a Delivery; now decide the map + notice + overlay state ------

  if (tracking.isError) {
    return (
      <TrackingPageShell backHref={backHref}>
        <TrackingErrorState
          onRetry={() => {
            void tracking.refetch();
          }}
        />
      </TrackingPageShell>
    );
  }

  const sessionState = sessionPending
    ? ("loading" as const)
    : session
      ? ("authenticated" as const)
      : ("guest" as const);

  const { markers, notice, finalState, action, isEmpty } = buildDeliveryTrackingMapState(
    tracking.data ?? null,
    { session: sessionState },
  );

  const markerLabels = {
    origin: t("tracking.legend.origin", "Local"),
    destination: t("tracking.legend.destination", "Domicilio"),
    riderLive: t("tracking.legend.riderLive", "Repartidor en vivo"),
    riderStale: t("tracking.legend.riderStale", "Última ubicación"),
  };
  const popupLabels = {
    origin: t("tracking.popup.origin", "Origen"),
    destination: t("tracking.popup.destination", "Destino"),
    riderLive: t("tracking.popup.riderLive", "Repartidor en vivo"),
    lastKnown: t("tracking.popup.lastKnown", "Última ubicación conocida"),
  };

  const noticeText =
    notice && finalState == null
      ? notice === "missingOrderPoints"
        ? t(
            "tracking.emptyState.title",
            isEmpty ? "Nothing to show on the map" : "No tracking data to show yet.",
          )
        : t(NOTICE_KEYS[notice], "")
      : null;

  const emptyStateText =
    isEmpty && notice === "missingOrderPoints"
      ? t(
          "tracking.emptyState.description",
          "We couldn't get the store, destination, or Rider location right now.",
        )
      : null;

  return (
    <TrackingPageShell backHref={backHref}>
      <div className="relative h-full w-full">
        <div className="pointer-events-none absolute left-3 top-3 z-10">
          <DeliveryTrackingLegend
            markers={markers}
            labels={markerLabels}
            className="pointer-events-auto"
          />
        </div>
        {finalState ? (
          <DeliveryTrackingFinalStateOverlay
            finalState={finalState}
            title={t(`tracking.finalState.${finalState}.title`, "")}
            ctaLabel={
              action
                ? t(
                    action.kind === "rateOrder"
                      ? "tracking.finalState.cta.rateOrder"
                      : "tracking.finalState.cta.backToOrder",
                    action.kind === "rateOrder" ? "Calificar pedido" : "Volver al pedido",
                  )
                : undefined
            }
            ctaHref={action ? backHref : undefined}
          />
        ) : null}
        <DeliveryTrackingMap
          markers={markers}
          markerLabels={markerLabels}
          popupLabels={popupLabels}
          noticeText={noticeText}
          emptyStateText={emptyStateText}
          className="h-full w-full"
          innerClassName="h-full w-full"
        />
      </div>
    </TrackingPageShell>
  );
}

function TrackingPageShell({
  backHref,
  children,
}: {
  backHref: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center gap-2 border-b p-3">
        <Link
          to={backHref}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("tracking.backToStatus", "Volver al pedido")}
        </Link>
      </div>
      <div className="relative flex-1">{children}</div>
    </div>
  );
}

function TrackingNotFoundState() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div
        data-testid="delivery-tracking-not-found"
        className="max-w-sm rounded-lg border bg-background p-6 text-center shadow-sm"
      >
        <h2 className="text-lg font-semibold">
          {t("tracking.notFound.title", "This order doesn't have a Delivery yet")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(
            "tracking.notFound.description",
            "The store hasn't created the shipment for your order yet.",
          )}
        </p>
      </div>
    </div>
  );
}

function TrackingErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div
        data-testid="delivery-tracking-error"
        className="max-w-sm rounded-lg border bg-background p-6 text-center shadow-sm"
      >
        <h2 className="text-lg font-semibold text-destructive">
          {t("tracking.errorState.title", "We couldn't load the tracking")}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(
            "tracking.errorState.description",
            "Try again in a few seconds. If the problem continues, head back to the order.",
          )}
        </p>
        <Button type="button" className="mt-4" onClick={onRetry}>
          {t("tracking.errorState.retry", "Retry")}
        </Button>
      </div>
    </div>
  );
}
